import { Component, OnInit, ViewChild, ElementRef } from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { AlertifyService } from "@services/alertify.service";
import { AuthService } from "@services/auth.service";
import { Router, ActivatedRoute } from "@angular/router";
import { classItem } from "@models/classItem";
import { ClassService } from "@services/class.service";
import { Video } from "@models/video";
import { UserClassesService } from "@services/user-classes.service";
import { UserClassesStateService } from "@services/user-classes-state.service";
import { UserClassesTaken } from "@models/user-classes-taken";
import { UserDto } from "@models/user-dto";
import { environment } from "@environments/environment";
import { ConfigurationService } from '@core/services/configuration.service';
import { TranslateService } from '@ngx-translate/core';
import { Bootstrap_Modal_Service } from '@services/bootstrap-modal.service';

@Component({
  selector: "app-video",
  templateUrl: "./video.component.html",
  styleUrls: ["./video.component.css"],
})
export class VideoComponent implements OnInit {
  videoId: number;
  classId: number;
  videoUrl = "";
  class: classItem; // class 1 , class 2 ...
  video: Video; // hold the return JSON from controller
  userClasses: UserClassesTaken; // update videoCompleted
  countToUpdateVideoWatched: number; //
  // New properties for enhanced UI
  isVideoLoading = true;
  isVideoCompleted = false;
  isHandoutCompleted = false;
  currentVideoTime = 0;
  videoDuration = 0;
  videoProgress = 0;

  isHandoutAvailable = true;

  // tests for Safari
  // declare FunctionsetCurrentTimeSafari2: any;
  // setCurrentTimeSafari: Function;

  @ViewChild("videoClass", { static: true }) videoClass: ElementRef;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    public authService: AuthService,
    private alertify: AlertifyService,
    private actRoute: ActivatedRoute,
    public classService: ClassService,
    public userClassesService: UserClassesService,
    private userClassesStateService: UserClassesStateService,
    private configurationService: ConfigurationService,
    private translate: TranslateService,
    private bootstrap_modal: Bootstrap_Modal_Service
  ) { }

  ngOnInit() {
    // we are asumming that the classId and VideoId are corresponding: Class1 = Video1
    this.videoId = this.actRoute.snapshot.params["id"];
    this.classId = this.videoId; // Set classId to match videoId
    
    // Initialize user classes state
    // this.userClassesStateService.loadUserClasses().subscribe();
    this.userClassesStateService.loadUserClasses().subscribe(() => {
      const classes = this.userClassesStateService.getUserClasses();
      
      // Safe lookup using find() with type conversion
      const currentClass = classes.find(cls => cls.classId === Number(this.classId));
      this.isVideoCompleted = currentClass?.isVideoCompleted || false;
      this.isHandoutCompleted = currentClass?.isHandoutCompleted || false;
      
      console.log(`Initial video completed state for class ${this.classId}: ${this.isVideoCompleted}`);
      console.log(`Initial handout completed state for class ${this.classId}: ${this.isHandoutCompleted}`);
    });

    
    // Subscribe to user classes state for this specific class
    this.userClassesStateService.userClasses$.subscribe(classes => {
      const currentClass = classes.find(cls => cls.classId === this.classId);
      if (currentClass) {
        this.updateVideoStateFromUserClass(currentClass);
      }
    });
    
    this.getClass(this.videoId);
    this.countToUpdateVideoWatched = 0;
  }

  private updateVideoStateFromUserClass(userClass: UserClassesTaken): void {
    this.isVideoCompleted = userClass.isVideoCompleted;
    this.isHandoutCompleted = userClass.isHandoutCompleted;
    
    // Set video position if video element is available
    if (this.videoClass?.nativeElement && userClass.videoWatched > 0) {
      const isSafari = navigator.vendor && 
                      navigator.vendor.indexOf("Apple") > -1 && 
                      navigator.userAgent && 
                      navigator.userAgent.indexOf("CriOS") == -1 && 
                      navigator.userAgent.indexOf("FxiOS") == -1;
      
      if (!isSafari) {
        this.videoClass.nativeElement.currentTime = userClass.videoWatched * 60; // Convert minutes back to seconds
      }
      // Safari handling can be added here if needed
    }
  }

  getClass(id: number) {
    this.classService.getClassById(id).subscribe(
      (classValue: classItem) => {
        this.class = classValue; // class 1, 2..
        // console.log(this.class);
      },
      (error) => {
        this.alertify.error(error);
      },
      () => {
        // Get video URL and set it up
        this.getVideoClass(this.class.videoUrl);
        // Video state will be updated via the state service subscription
      }
    );
  }

  isHandoutButtonDisabled(): boolean {
    // Override: If handout is explicitly available, enable the button
    if (this.isHandoutAvailable) {
      return false;
    }
    
    // Original logic: Only enable if video is completed
    return !this.isVideoCompleted;
  }

  markHandoutCompleted(): void {
    const currentClass = this.userClassesStateService.getClassById(this.classId);
    if (!currentClass) return;
    
    const userHandoutUpdate: UserClassesTaken = {
      ...currentClass,
      isHandoutCompleted: true,
    };

    // Update via service call, then refresh state
    this.userClassesService
      .UpdateUserClassesTakenVideoCompleted(userHandoutUpdate)
      .subscribe({
        next: () => {
          console.log("Handout marked as completed!");
          this.isHandoutCompleted = true;
          // Refresh the state to get updated progress
          this.userClassesStateService.refreshUserClasses();
        },
        error: (error) => {
          this.translate.get(["ALERTIFY.ERROR_UPDATE_HANDOUT_STATUS", "BOOTSTRAP.MODAL.ERROR"]).subscribe((text: string) => {
            this.alertify.error(`${text} ${error}`)

            this.bootstrap_modal.open({
              modal_header_html: text['BOOTSTRAP.MODAL.ERROR'],
              modal_body_html: text['ALERTIFY.ERROR_UPDATE_HANDOUT_STATUS'],
              modal_color_variant: "danger"
            })
          });
        }
      });
  }

  getVideoClass(videoName: string) {
    console.log(`Fetching video for class: ${videoName}`);
    this.classService.getVideoClass(videoName).subscribe(
      (video: Video) => {
        this.videoUrl = video.videoUrl; // class 1, 2..
        // console.log(this.videoUrl);
        // this.videoUrl =  'https://lwccmediafilesstorage.blob.core.windows.net/mediafilescontainerspanish/Intercessory_Prayer_Class_1.mp4?sv=2018-03-28&sr=b&sig=mrena0git80DJLif9Zbn6F8JzwocpYAayeXcOj0tFJM%3D&se=2021-01-12T23%3A52%3A06Z&sp=r';

        // load video on control
        //
        this.videoClass.nativeElement.src = this.videoUrl;
        // this.videoClass.nativeElement.currentTime = 55.55;

        // this.videoClass.nativeElement.load();
        // this.videoClass.nativeElement.play();
      },
      (error) => {
        //console.log("Errror: " + error);
        //console.log("Errror: " + error.errors);
        this.alertify.error(error);
      },
      () => {
        // third part , complete
        //set default country
      }
    );
  }

  // New methods for enhanced UI functionality
  getVideoProgress(): number {
    if (this.videoDuration === 0) return 0;
    return Math.round((this.currentVideoTime / this.videoDuration) * 100);
  }

  onVideoLoaded(event: any): void {
    this.isVideoLoading = false;
    this.videoDuration = event.target.duration;
    this.updateVideoTimeDisplay();
  }

  onVideoError(event: any): void {
    this.isVideoLoading = false;
    this.translate.get(["ALERTIFY.VIDEO_ERROR", "BOOTSTRAP.MODAL.ERROR"]).subscribe((text: string) => {
      this.alertify.error(text['ALERTIFY.VIDEO_ERROR'])

      this.bootstrap_modal.open({
        modal_header_html: text['BOOTSTRAP.MODAL.ERROR'],
        modal_body_html: text['ALERTIFY.VIDEO_ERROR'],
        modal_color_variant: "danger"
      })
    })
    console.error('Video error:', event);
  }

  navigateToHandout(): void {
    if (this.isHandoutAvailable || this.isVideoCompleted && this.class?.id) {
      // Use the same handout URL pattern as the class-card component
      
      let handoutUrl = 'https://online.fliphtml5.com/qubm/xuvw';
      // Get class-specific document URL from configuration service
      const documentUrl = this.configurationService.getClassDocumentUrl(this.class.id);


      if (documentUrl) { 
        handoutUrl = documentUrl; 
      }
      console.log(`Opening handout URL: ${handoutUrl}`);
      console.log(`Class ID: ${this.class.id}`);
      console.log(`documentUrl: ${documentUrl}`);

      window.open(handoutUrl, '_blank');
      this.translate.get("ALERTIFY.OPENING_HANDOUT").subscribe((text: string) => {
        this.alertify.success(text);
      });
      
      // Update handout completion status in database
      const currentClassState = this.userClassesStateService.getClassById(this.classId);
      
      const userHandoutUpdate: UserClassesTaken = {
        classId: this.class.id,
        userId: this.authService.returnCurrentUser().id,
        videoLength: this.videoDuration / 60,
        videoWatched: this.currentVideoTime / 60,
        isQuizCompleted: currentClassState?.isQuizCompleted || false,
        isVideoCompleted: this.isVideoCompleted,
        isHandoutCompleted: true, // Mark handout as completed
        whenClassStarted: currentClassState?.whenClassStarted || null,
        whenClassCompleted: currentClassState?.whenClassCompleted || null,
        isAvailable: currentClassState?.isAvailable || false,
      };

      // Update the handout completion status
      this.userClassesService
        .UpdateUserClassesTakenVideoCompleted(userHandoutUpdate)
        .subscribe({
          next: () => {
            console.log("Handout marked as completed!");
            this.isHandoutCompleted = true; // Update local state
            // Refresh state to update progress automatically
            this.userClassesStateService.refreshUserClasses();
          },
          error: (error) => {
            this.translate.get("ALERTIFY.ERROR_UPDATING_HANDOUT_STATUS").subscribe((text: string) => {              
              this.alertify.error(text["ALERTIFY.ERROR_UPDATING_HANDOUT_STATUS"]);
            });
          }
        });
    }
  }

  navigateToQuiz(): void {
    if (this.isVideoCompleted && this.class?.id) {
      this.router.navigate(['/app/quiz', this.class.id]);
    }
  }

  tickTimeVideo(data: any) {
    // Update new properties for enhanced UI
    this.currentVideoTime = data.target.currentTime;
    this.videoDuration = data.target.duration;
    this.videoProgress = this.getVideoProgress();
    this.updateVideoTimeDisplay();

    // Check if video is completed
    if (this.videoProgress > 96) {
      this.isVideoCompleted = true;
    }

    // Continue with existing logic
    this.countToUpdateVideoWatched++; // must wait 15 times to update  video watched

    if (this.countToUpdateVideoWatched == 15) {
      const userDto: UserDto = JSON.parse(localStorage.getItem("user")); //get userId if we refresh the page ** pending
      // = { prop1: 'str', prop2: 3 };

      // update pe
      var duration = data.target.duration;
      var percentage;
      percentage = (data.target.currentTime * 100) / duration;
      let videoComplete = false;
      if (percentage > 96) {
        videoComplete = true;
        this.isVideoCompleted = true;
      }

      // Get current class state for proper data preservation
      const currentClassState = this.userClassesStateService.getClassById(this.classId);
      
      this.userClasses = {
        classId: this.class.id,
        userId: this.authService.returnCurrentUser().id,
        videoLength: duration / 60,
        videoWatched: data.target.currentTime / 60,
        isQuizCompleted: currentClassState?.isQuizCompleted || false,
        isVideoCompleted: videoComplete,
        isHandoutCompleted: currentClassState?.isHandoutCompleted || false,
        whenClassStarted: currentClassState?.whenClassStarted || null,
        whenClassCompleted: currentClassState?.whenClassCompleted || null,
        isAvailable: currentClassState?.isAvailable || false,
      };

      this.countToUpdateVideoWatched = 0; //reset the count to update the video watched
      // update the video watched
      this.userClassesService
        .UpdateUserClassesTakenVideo(this.userClasses)
        .subscribe((next) => {});

      //mark the video as completed
      if (videoComplete) {
        this.userClassesService
          .UpdateUserClassesTakenVideoCompleted(this.userClasses)
          .subscribe({
            next: () => {
              // console.log("Video Complete!");
              // Refresh state to get updated progress - this automatically updates CourseProgressService
              this.userClassesStateService.refreshUserClasses();
            },
            error: (error) => {
              this.alertify.error(error);
            }
          });
      }
    }
  }

  private updateVideoTimeDisplay(): void {
    // Update DOM elements if they exist
    const durationElement = document.getElementById('videoDuration');
    const progressElement = document.getElementById('videoProgress');
    
    if (durationElement && this.videoDuration) {
      durationElement.textContent = this.formatTime(this.videoDuration);
    }
    
    if (progressElement && this.currentVideoTime) {
      progressElement.textContent = this.formatTime(this.currentVideoTime);
    }
  }

  private formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  private updateCourseProgress(): void {
    // Course progress is now automatically updated by UserClassesStateService
    // when the state changes, so this method is simplified
    // console.log('Course progress updated automatically via state service');
  }


}
